class BdMenu extends HTMLElement {
  connectedCallback() {
    this.triggers = this.querySelectorAll('[data-submenu]');
    this.panels = this.querySelectorAll('[data-submenu-id]');

    this.isTouch = matchMedia('(hover: none)').matches;
    this.currentOpen = null;

    if (!this.isTouch) {
      this.addEventListener('pointerover', this.onHoverEnter);
      this.addEventListener('pointerleave', this.onHoverLeave, true);
    }

    this.addEventListener('click', this.onClick);
    this.addEventListener('focusin', this.onFocusIn, true);
  }

  /* Accessible Keyboard Targeting */
  onFocusIn = (event) => {
    if (this.isTouch) return;

    const trigger = event.target.closest('[data-submenu]');
    const panel = event.target.closest('[data-submenu-id]');

    // 1. Focus moved outside the entire menu → close everything
    if (!this.contains(event.target)) {
      this.closeAll();
      return;
    }

    // 2. Focus is inside the currently open panel → keep it open
    if (panel && panel.dataset.submenuId === this.currentOpen) {
      return;
    }

    // 3. Focused a top-level item WITH submenu → open it (and close previous)
    if (trigger?.dataset.submenu) {
      const id = trigger.dataset.submenu;
      if (this.currentOpen !== id) {
        this.open(id);
      }
      return;
    }

    // 4. Focus is inside the menu, but not in an open panel or trigger (e.g. standard links)
    this.closeAll();
  };

  // -----------------------------
  // Active Trigger Helpers
  // -----------------------------
  clearActiveTriggers() {
    this.triggers.forEach((t) => t.classList.remove('is-active'));
  }

  setActiveTrigger(id) {
    this.clearActiveTriggers();
    const trigger = this.querySelector(`[data-submenu="${id}"]`);
    if (trigger) trigger.classList.add('is-active');
  }

  // -----------------------------
  // Desktop Hover
  // -----------------------------
  onHoverEnter = (event) => {
    if (this.isTouch) return;

    clearTimeout(this.closeTimeout);

    const menuItem = event.target.closest('.menu-item');
    if (!menuItem) return;

    const trigger = menuItem.querySelector('[data-submenu]');
    const id = trigger?.dataset.submenu;

    // Hovered item has NO submenu → close everything AND unlock scroll
    if (!id) {
      this.closeAll();
      return;
    }

    // Hovered item HAS submenu → switch to it (prevents re-opening the same menu)
    if (this.currentOpen !== id) {
      if (this.currentOpen) {
        this.close(this.currentOpen);
      }
      this.open(id);
    }
  };

  onHoverLeave = (event) => {
    if (this.isTouch) return;

    // If pointer is still inside the menu, do nothing
    if (event.relatedTarget && this.contains(event.relatedTarget)) {
      return;
    }

    // Start delayed close
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      // Only close if STILL outside the menu
      if (!this.matches(':hover')) {
        this.closeAll();
      }
    }, 250);
  };

  // -----------------------------
  // Mobile Tap
  // -----------------------------
  onClick = (event) => {
    // 1) Inner mega-menu tabs (left column buttons)
    const tabTrigger = event.target.closest('[data-tab-target]');
    if (tabTrigger) {
      const target = tabTrigger.dataset.tabTarget;
      if (!target) return;

      const submenuPanel = tabTrigger.closest('[data-submenu-id]');
      if (!submenuPanel) return;

      // Hide all children except the active one
      submenuPanel.querySelectorAll('.mm-children').forEach((child) => {
        child.hidden = child.dataset.tabContent !== target;
      });

      // Recalculate height AFTER visibility changes
      requestAnimationFrame(() => {
        submenuPanel.style.setProperty('--submenu-height', submenuPanel.scrollHeight + 'px');
      });
      return;
    }

    // 2) Top-level submenu triggers (desktop/mobile)
    const trigger = event.target.closest('[data-submenu]');
    if (!trigger) return;

    // If user clicked the text portion of the <a>, allow navigation
    if (event.target.closest('.link-title')) {
      return; 
    }

    event.preventDefault();

    const id = trigger.dataset.submenu;
    if (!id) return;

    if (this.isTouch) {
      this.toggle(id);
    } else {
      this.openedByClick = true;
      this.open(id);
    }
  };

  // -----------------------------
  // Core Logic
  // -----------------------------
  open(id) {
    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    // Close previous submenu first
    if (this.currentOpen && this.currentOpen !== id) {
      this.close(this.currentOpen);
    }

    this.currentOpen = id;
    this.setActiveTrigger(id);

    // Lock scroll ONLY when a submenu panel is actually opening
    document.body.classList.add('no-overflow');

    panel.dataset.open = 'true';
    panel.style.setProperty('--submenu-height', panel.scrollHeight + 50 + 'px');

    panel.querySelectorAll('a, button').forEach((el) => (el.tabIndex = 0));
  }

  closeAll() {
    const hadOpen = this.currentOpen !== null;
    this.currentOpen = null;

    this.panels.forEach((panel) => {
      panel.dataset.open = 'false';
      panel.style.setProperty('--submenu-height', '0px');
      panel.querySelectorAll('a, button').forEach((el) => (el.tabIndex = -1));
    });

    this.clearActiveTriggers();

    // Remove scroll lock ONLY if something was open before
    if (hadOpen) {
      document.body.classList.remove('no-overflow');
    }
  }

  close(id) {
    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    panel.dataset.open = 'false';
    panel.style.setProperty('--submenu-height', '0px');

    const trigger = this.querySelector(`[data-submenu="${id}"]`);
    if (trigger) trigger.classList.remove('is-active');
  }

  toggle(id) {
    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    panel.dataset.open === 'true' ? this.close(id) : this.open(id);
  }
}

customElements.define('bd-menu', BdMenu);


/** DRAWER MENU ***/
class BdDrawerMenu extends HTMLElement {
  connectedCallback() {
    this.currentPanel = null; // Replaced array stack with single state tracker
    this.menuHandle = this.dataset.menu;

    this.hamburger = document.querySelector(`[data-mobile-open][data-menu="${this.menuHandle}"]`);
    this.closeButton = this.querySelector('[data-close]');
    this.triggers = this.querySelectorAll('[data-submenu]');
    this.panels = this.querySelectorAll('[data-submenu-id]');
    this.backButtons = this.querySelectorAll('[data-back]');

    if (this.hamburger) this.hamburger.addEventListener('click', () => this.toggleDrawer());
    if (this.closeButton) this.closeButton.addEventListener('click', () => this.closeDrawer());

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(trigger.dataset.submenu));
    });

    this.backButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.closeCurrentPanel());
    });

    // Bind handlers for global removal
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
  }

  // ------------------------------------------------------------
  // Drawer Open / Close
  // ------------------------------------------------------------
  toggleDrawer() {
    this.dataset.open === 'true' ? this.closeDrawer() : this.openDrawer();
  }

  openDrawer() {
    this.dataset.open = 'true';
    if (this.hamburger) this.hamburger.dataset.open = 'true';
    document.body.classList.add('overflow-hidden');

    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('click', this.onClickOutside);
  }

  closeDrawer() {
    this.dataset.closing = 'true';
    this.dataset.open = 'false';
    if (this.hamburger) this.hamburger.dataset.open = 'false';

    const menuList = this.querySelector('.menu-list');
    let isCleanedUp = false;

    const cleanup = () => {
      if (isCleanedUp) return; // Prevent double execution
      isCleanedUp = true;

      delete this.dataset.closing;
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('click', this.onClickOutside);

      this.closeAll();
      menuList.removeEventListener('transitionend', onEnd);
    };

    const onEnd = (event) => {
      if (event.target === menuList) cleanup();
    };

    menuList.addEventListener('transitionend', onEnd);
    
    // SAFETY: Fallback timeout in case transitionend is interrupted or fails to fire
    setTimeout(cleanup, 300); 
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.closeDrawer();
  }

  onClickOutside(event) {
    const nav = this.querySelector('.menu-list');
    if (nav?.contains(event.target)) return;
    if (event.target === this.hamburger || this.hamburger?.contains(event.target)) return;
    
    this.closeDrawer();
  }

  // ------------------------------------------------------------
  // Submenu Layer Navigation
  // ------------------------------------------------------------
  open(id) {
    const panel = this.querySelector(`[data-submenu-id="${id}"]`);
    if (!panel) return;

    // Close any currently open panel before opening the new one
    if (this.currentPanel) {
      this.currentPanel.removeAttribute('data-open');
    }

    this.currentPanel = panel;
    panel.dataset.open = 'true';

    // Height measurement
    const activeChild = panel.querySelector('.mm-children:not([hidden])');
    const fullHeight = activeChild ? activeChild.scrollHeight : 0;
    panel.style.setProperty('--submenu-height', fullHeight + 'px');
    panel.dataset.heightLocked = 'true';

    // Enable tabbing
    panel.querySelectorAll('a, button').forEach(el => (el.tabIndex = 0));
  }

  closeCurrentPanel() {
    if (this.currentPanel) {
      this.currentPanel.removeAttribute('data-open');
      this.currentPanel = null;
    }
  }

  closeAll() {
    this.panels.forEach(panel => {
      panel.removeAttribute('data-open');
      panel.style.removeProperty('--submenu-height');
      panel.removeAttribute('data-heightLocked');
    });
    this.currentPanel = null;

    if (this.triggers) {
      this.triggers.forEach(t => t.classList.remove('active'));
    }
  }
}

customElements.define('bd-drawer-menu', BdDrawerMenu);


/* -------------------------------------------------------------------
   HEADER HEIGHT & SCROLL LOGIC (Highly Optimized)
   ------------------------------------------------------------------- */

// 1. Use ResizeObserver instead of calculating on scroll.
// This only fires when the header ACTUALLY changes size (e.g., sticky shrink)
// instead of firing dozens of times per second while scrolling.
const headerGroup = document.querySelector('.shopify-section-group-header-group');

if (headerGroup) {
  const resizeObserver = new ResizeObserver(entries => {
    for (let entry of entries) {
      document.documentElement.style.setProperty(
        '--header-group-height', 
        entry.contentRect.height + 'px'
      );
    }
  });
  resizeObserver.observe(headerGroup);
}

// 2. Optimized Scroll Handler for body class toggling.
// Uses requestAnimationFrame instead of a manual debounce. 
// It syncs perfectly with the browser's paint cycle and is lighter weight.
 
let isScrolling = false;
let lastScrollY = 0;
const threshold = 5; // Adjust this if the header flickers on trackpads

window.addEventListener('scroll', () => {
  if (!isScrolling) {
    window.requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      
      // Remove both classes first so we don't have conflicting classes
      document.body.classList.remove('scrolling-down', 'scrolling-up');

      // Only add classes if we are actually scrolled past the top
      if (currentScrollY > threshold) {
        
        // Determine direction by comparing current pos to last pos
        if (currentScrollY > lastScrollY) {
          document.body.classList.add('scrolling-down');
        } else if (currentScrollY < lastScrollY) {
          document.body.classList.add('scrolling-up');
        }
        
      }
      
      // Update our state for the next scroll event
      lastScrollY = currentScrollY;
      isScrolling = false;
    });
    isScrolling = true;
  }
}, { passive: true });